import React from "react";
import { shallowEqual } from "react-redux";
import { type UscDispatchType, useUscSelector } from "../../../../../redux/store";
import type { FinanceOptionType } from "../../../../../../../common-deprecated/types/CommonTypes";
import { useUscLabel } from "../../../../../utils/labels";
import { getSimplifiedTotalPriceSelector } from "../../../../../utils/priceUtils";
import UscCashPrice from "../../price/UscCashPrice";
import { setFinanceOption } from "../../../../../redux/actions/UscBasketActions";
import { enableUscMonthlyRate } from "../../../../../utils/settings";
import * as Styles from "./UscFinanceToggle.styles";
import { type UscTrackingLocation, trackFinanceToggle } from "../../../../../utils/tracking";
import Preloader from "../../../../../../shared-components/lexus/styles/molecules/Preloader";
import { useDispatch } from "../../../../../../../shared-logic/redux/store";

const UscFinanceToggle = ({ trackingLocation }: { trackingLocation: UscTrackingLocation }): JSX.Element => {
    const dispatch = useDispatch<UscDispatchType>();
    const activeFinanceOption = useUscSelector((state) => state.basket.activeFinanceOption);
    const enableMonthly = useUscSelector(({ commonSettings, uscSettings, vehicle }) =>
        enableUscMonthlyRate(commonSettings, uscSettings, vehicle.result, vehicle.type),
    );
    const showFinancePrices = useUscSelector((state) => !state.uscSettings.hideFinanceTogglePrices);
    const vehicleType = useUscSelector((state) => state.vehicle.type);
    const { monthly, cashLoading, monthlyLoading } = useUscSelector(
        getSimplifiedTotalPriceSelector(true),
        shallowEqual,
    );

    const [cashOnlyLabel, selectedCashLabel, selectedMonthlyLabel, switchToCashLabel, switchToMonthly] = useUscLabel([
        "uscFinanceOptionCashOnly",
        "uscFinanceOptionSelectedCash",
        "uscFinanceOptionSelectedMonthly",
        "uscFinanceOptionSwitchToCash",
        "uscFinanceOptionSwitchToMonthly",
    ]);

    // Only used in case the finance prices are hidden
    const [cashToggleLabel, monthlyToggleLabel] = useUscLabel([
        "uscFinanceOptionCashToggle",
        "uscFinanceOptionMonthlyToggle",
    ]);

    const onToggle = (selected: FinanceOptionType): void => {
        trackFinanceToggle(trackingLocation, selected, vehicleType);
        dispatch(setFinanceOption(selected));
    };

    return (
        <Styles.Wrapper enableMonthly={enableMonthly}>
            {enableMonthly ? (
                <>
                    <Styles.Button
                        type="button"
                        aria-pressed={activeFinanceOption === "cash"}
                        aria-label={switchToCashLabel}
                        onClick={enableMonthly ? () => onToggle("cash") : undefined}
                    >
                        <Styles.ButtonDescription>
                            {activeFinanceOption === "cash" ? selectedCashLabel : switchToCashLabel}
                        </Styles.ButtonDescription>
                        <Styles.ButtonTitle isLoading={cashLoading}>
                            {showFinancePrices ? <UscCashPrice hideDisclaimerIcons /> : cashToggleLabel}
                        </Styles.ButtonTitle>
                        {cashLoading && (
                            <Styles.LoadingIconWrapper>
                                <Preloader size={16} />
                            </Styles.LoadingIconWrapper>
                        )}
                    </Styles.Button>

                    <Styles.Button
                        type="button"
                        aria-pressed={activeFinanceOption === "monthly"}
                        aria-label={switchToMonthly}
                        onClick={() => onToggle("monthly")}
                    >
                        <Styles.ButtonDescription>
                            {activeFinanceOption === "monthly" ? selectedMonthlyLabel : switchToMonthly}
                        </Styles.ButtonDescription>
                        <Styles.ButtonTitle isLoading={monthlyLoading}>
                            {showFinancePrices ? (
                                <span dangerouslySetInnerHTML={{ __html: monthly }} />
                            ) : (
                                monthlyToggleLabel
                            )}
                        </Styles.ButtonTitle>
                        {monthlyLoading && (
                            <Styles.LoadingIconWrapper>
                                <Preloader size={16} />
                            </Styles.LoadingIconWrapper>
                        )}
                    </Styles.Button>
                </>
            ) : (
                <Styles.Button type="button" aria-label={cashOnlyLabel} disabled>
                    <Styles.ButtonDescription>{cashOnlyLabel}</Styles.ButtonDescription>
                    <Styles.ButtonTitle isLoading={cashLoading}>
                        {showFinancePrices ? <UscCashPrice hideDisclaimerIcons /> : cashToggleLabel}
                    </Styles.ButtonTitle>
                    {cashLoading && (
                        <Styles.LoadingIconWrapper>
                            <Preloader size={16} />
                        </Styles.LoadingIconWrapper>
                    )}
                </Styles.Button>
            )}
        </Styles.Wrapper>
    );
};

export default UscFinanceToggle;
